import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  TablePagination,
} from "@mui/material";
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  useAuditLogs,
  useAuditStats,
  useExportAuditLogs,
} from "@/hooks/useAuditLogs";
import type { AuditLogFilters } from "@/types";
import { useApiClient } from "@/hooks/useApiClient";

const AdminAuditLogsPage: React.FC = () => {
  const [filters, setFilters] = React.useState<AuditLogFilters>({
    limit: 50,
    offset: 0,
  });
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);

  useApiClient(); // Inject current OIDC token into API client

  // Fetch audit logs and stats
  const {
    data: logsData,
    isLoading: logsLoading,
    error: logsError,
    refetch,
  } = useAuditLogs(filters);
  const { data: statsData, isLoading: statsLoading } = useAuditStats();
  const exportMutation = useExportAuditLogs();

  const handleFilterChange = (field: keyof AuditLogFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
      offset: 0, // Reset to first page on filter change
    }));
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({
      ...prev,
      offset: newPage * rowsPerPage,
    }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters((prev) => ({
      ...prev,
      limit: newRowsPerPage,
      offset: 0,
    }));
  };

  const handleExport = () => {
    exportMutation.mutate(filters);
  };

  const handleRefresh = () => {
    refetch();
  };

  const auditLogs = logsData?.entries || [];
  const stats = statsData || {
    totalLogs: 0,
    todayLogs: 0,
    criticalEvents: 0,
    uniqueUsers: 0,
  };

  const getActionLabel = (action: string) => {
    const labels = {
      LOGIN: "Inloggning",
      LOGOUT: "Utloggning",
      DOCUMENT_UPLOAD: "Dokumentuppladdning",
      DOCUMENT_VIEW: "Dokumentvisning",
      DOCUMENT_DOWNLOAD: "Dokumentnedladdning",
      DOCUMENT_DELETE: "Dokumentradering",
      CONTROL_CREATE: "Kontroll skapad",
      CONTROL_UPDATE: "Kontroll uppdaterad",
      CONTROL_DELETE: "Kontroll raderad",
      USER_CREATE: "Användare skapad",
      USER_UPDATE: "Användare uppdaterad",
      SHARE_CREATED: "Delning skapad",
      SHARE_ACCESSED: "Delning öppnad",
    };
    return labels[action as keyof typeof labels] || action;
  };

  const getActionColor = (action: string) => {
    const colors = {
      LOGIN: "success" as const,
      LOGOUT: "default" as const,
      DOCUMENT_UPLOAD: "info" as const,
      DOCUMENT_DELETE: "error" as const,
      CONTROL_CREATE: "primary" as const,
      USER_UPDATE: "warning" as const,
      SHARE_CREATED: "secondary" as const,
    };
    return colors[action as keyof typeof colors] || ("default" as const);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("sv-SE");
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3
        }}>
        <Typography variant="h4" component="h1">
          Granskningsloggar
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={logsLoading}
          >
            Uppdatera
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? "Exporterar..." : "Exportera"}
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {logsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Kunde inte ladda revisionsloggar. Försök igen senare.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{
        mb: 4
      }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Totalt antal loggar
              </Typography>
              {statsLoading ? (
                <CircularProgress size={32} />
              ) : (
                <Typography variant="h4">
                  {stats.totalLogs.toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Idag
              </Typography>
              {statsLoading ? (
                <CircularProgress size={32} />
              ) : (
                <Typography variant="h4" sx={{
                  color: "primary.main"
                }}>
                  {stats.todayLogs}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Kritiska händelser
              </Typography>
              {statsLoading ? (
                <CircularProgress size={32} />
              ) : (
                <Typography variant="h4" sx={{
                  color: "error.main"
                }}>
                  {stats.criticalEvents}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Unika användare
              </Typography>
              {statsLoading ? (
                <CircularProgress size={32} />
              ) : (
                <Typography variant="h4" sx={{
                  color: "info.main"
                }}>
                  {stats.uniqueUsers}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="h6"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FilterIcon />
                Filter och sökning
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Sök i loggar"
                    value={filters.search || ""}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <SearchIcon color="action" sx={{ mr: 1 }} />
                        ),
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Åtgärd</InputLabel>
                    <Select
                      value={filters.action || ""}
                      onChange={(e) =>
                        handleFilterChange("action", e.target.value)
                      }
                      label="Åtgärd"
                    >
                      <MenuItem value="">Alla åtgärder</MenuItem>
                      <MenuItem value="LOGIN">Inloggning</MenuItem>
                      <MenuItem value="LOGOUT">Utloggning</MenuItem>
                      <MenuItem value="DOCUMENT_UPLOAD">
                        Dokumentuppladdning
                      </MenuItem>
                      <MenuItem value="DOCUMENT_VIEW">Dokumentvisning</MenuItem>
                      <MenuItem value="DOCUMENT_DOWNLOAD">
                        Dokumentnedladdning
                      </MenuItem>
                      <MenuItem value="DOCUMENT_DELETE">
                        Dokumentradering
                      </MenuItem>
                      <MenuItem value="CONTROL_CREATE">
                        Kontroll skapad
                      </MenuItem>
                      <MenuItem value="CONTROL_UPDATE">
                        Kontroll uppdaterad
                      </MenuItem>
                      <MenuItem value="CONTROL_DELETE">
                        Kontroll raderad
                      </MenuItem>
                      <MenuItem value="USER_CREATE">Användare skapad</MenuItem>
                      <MenuItem value="USER_UPDATE">
                        Användare uppdaterad
                      </MenuItem>
                      <MenuItem value="USER_DELETE">Användare raderad</MenuItem>
                      <MenuItem value="SHARE_CREATED">Delning skapad</MenuItem>
                      <MenuItem value="SHARE_ACCESSED">Delning öppnad</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Resurstyp"
                    value={filters.resourceType || ""}
                    onChange={(e) =>
                      handleFilterChange("resourceType", e.target.value)
                    }
                    placeholder="t.ex. Document, Control, User"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{
            mb: 2
          }}>
            Systemhändelser
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tidstämpel</TableCell>
                  <TableCell>Användare</TableCell>
                  <TableCell>Åtgärd</TableCell>
                  <TableCell>Resurs</TableCell>
                  <TableCell>IP-adress</TableCell>
                  <TableCell align="center">Detaljer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mt: 2
                        }}>
                        Laddar revisionsloggar...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        Inga revisionsloggar hittades
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2
                          }}>
                          <Avatar
                            sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
                          >
                            {log.user?.email?.charAt(0).toUpperCase() || "?"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{
                              fontWeight: "medium"
                            }}>
                              {log.user?.email || "Okänd"}
                            </Typography>
                            {log.user?.farmProfile?.farmName && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary"
                                }}
                              >
                                {log.user.farmProfile.farmName}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getActionLabel(log.action)}
                          size="small"
                          color={getActionColor(log.action)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.resourceType || "-"}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          {log.resourceId || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {log.ipAddress || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <Box
                            component="pre"
                            sx={{
                              fontSize: "0.75rem",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {JSON.stringify(log.details, null, 2)}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{
                            color: "text.secondary"
                          }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {logsData && (
            <TablePagination
              component="div"
              count={logsData.total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rader per sida:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} av ${count !== -1 ? count : `fler än ${to}`}`
              }
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminAuditLogsPage;
